import React, { useContext } from 'react';
import { XMarkIcon, UserCircleIcon, EnvelopeIcon, BuildingOfficeIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { formatDate } from '../utils/dateUtils';
import { AuthContext } from '../contexts/AuthContext';

const AgentDetailsModal = ({ show, onClose, agent, agency }) => {
  const { user, agencyCountry } = useContext(AuthContext);
  
  if (!show || !agent) return null;

  // Helper function for consistent date formatting
  const formatDateWithContext = (dateString) => {
    if (!dateString || dateString === "0001-01-01T00:00:00Z") {
      return 'Not available';
    }
    return formatDate(dateString, {
      userPreference: user?.dateFormatPreference,
      agencyCountry: agencyCountry
    });
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm transition-opacity animate-fade-in"
        onClick={onClose}
      />

      {/* Modal Container */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg animate-scale-in">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
            <div className="flex items-center">
              <div className="bg-gradient-to-br from-emerald-400 to-emerald-600 p-2.5 rounded-xl shadow-lg shadow-emerald-500/20">
                <UserCircleIcon className="h-5 w-5 text-white" />
              </div>
              <div className="ml-4">
                <h2 className="text-lg font-semibold text-gray-900">Agent Details</h2>
                <p className="text-sm text-gray-500 mt-0.5">View agent information</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Agent Avatar */}
            <div className="flex items-center mb-6">
              <div className="bg-gradient-to-br from-emerald-400 to-emerald-600 p-4 rounded-2xl shadow-lg shadow-emerald-500/20">
                <UserCircleIcon className="h-12 w-12 text-white" />
              </div>
              <div className="ml-4">
                <h3 className="text-xl font-semibold text-gray-900">
                  {agent.User?.given || 'Unknown'} {agent.User?.surname || ''}
                </h3>
                <p className="text-gray-500">{agent.User?.email || 'No email available'}</p>
                <div className="flex items-center mt-2">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    agent.User?.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {agent.User?.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>

            {/* Agent Information */}
            <div className="space-y-4">
              {/* Personal Information */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                  <UserCircleIcon className="h-4 w-4 mr-2 text-gray-400" />
                  Personal Information
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">First Name</label>
                    <input
                      type="text"
                      value={agent.User?.given || 'N/A'}
                      className="input text-sm bg-white"
                      disabled
                      readOnly
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Last Name</label>
                    <input
                      type="text"
                      value={agent.User?.surname || 'N/A'}
                      className="input text-sm bg-white"
                      disabled
                      readOnly
                    />
                  </div>
                </div>
                <div className="mt-3">
                  <label className="block text-xs font-medium text-gray-500 mb-1">Email Address</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <EnvelopeIcon className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      type="email"
                      value={agent.User?.email || 'N/A'}
                      className="input input-with-icon text-sm bg-white"
                      disabled
                      readOnly
                    />
                  </div>
                </div>
              </div>

              {/* Agency Information */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                  <BuildingOfficeIcon className="h-4 w-4 mr-2 text-gray-400" />
                  Agency Information
                </h4>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Agency Name</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <BuildingOfficeIcon className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={agent.Agency?.name || agency?.name || 'N/A'}
                      className="input input-with-icon text-sm bg-white"
                      disabled
                      readOnly
                    />
                  </div>
                </div>
                <div className="mt-3">
                  <label className="block text-xs font-medium text-gray-500 mb-1">Currency</label>
                  <input
                    type="text"
                    value={agent.Agency?.currency || 'N/A'}
                    className="input text-sm bg-white"
                    disabled
                    readOnly
                  />
                </div>
              </div>

              {/* System Information */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                  <CalendarIcon className="h-4 w-4 mr-2 text-gray-400" />
                  System Information
                </h4>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">User ID</label>
                    <input
                      type="text"
                      value={agent.UserID || agent.User?.id || 'N/A'}
                      className="input text-sm bg-white font-mono"
                      disabled
                      readOnly
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Agent ID</label>
                    <input
                      type="text"
                      value={agent.ID || 'N/A'}
                      className="input text-sm bg-white font-mono"
                      disabled
                      readOnly
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Created</label>
                    <input
                      type="text"
                      value={formatDateWithContext(agent.CreatedAt)}
                      className="input text-sm bg-white"
                      disabled
                      readOnly
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Last Updated</label>
                    <input
                      type="text"
                      value={formatDateWithContext(agent.UpdatedAt)}
                      className="input text-sm bg-white"
                      disabled
                      readOnly
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-6 flex justify-end">
              <button
                onClick={onClose}
                className="px-5 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentDetailsModal;
